import { Autocomplete, Stack, TextField } from "@mui/material";
import React, { useEffect } from "react";
import { IProfile, RelationshipDirection, RelationshipType } from "../../interfaces/IUser";

type ProfileInnendienstAssignmentProps = {
    worker: IProfile;
    availableInnendienstEmployees: IProfile[];
    updateInnendienstAssignment: (employeeId: number | null) => void;
};

export const getInnendienstRelationship = (worker: IProfile) => {
    const innendienstRelationship = worker.relationships.find((relationship) => {
        return (
            relationship.direction === RelationshipDirection.Incoming &&
            relationship.type === RelationshipType.Innendienst
        );
    });

    return innendienstRelationship;
};

const getInnendienstEmployeeForWorker = (worker: IProfile, availableInnendienstEmployees: IProfile[]) => {
    const innendienstRelationship = getInnendienstRelationship(worker);

    if (!innendienstRelationship) {
        return null;
    }

    const workerInnendienstEmployee = availableInnendienstEmployees.find((employee) => {
        return employee.id === innendienstRelationship.peer;
    });

    return workerInnendienstEmployee;
};

export const isInnendienstWorker = (profile: IProfile | undefined) => {
    return [3, 4].includes(profile?.Role as number);
};

export const isAussendienstWorker = (profile: IProfile | undefined) => {
    return [5, 6].includes(profile?.Role as number);
};

const ProfileInnendienstAssignment: React.FC<ProfileInnendienstAssignmentProps> = ({
    worker,
    availableInnendienstEmployees,
    updateInnendienstAssignment,
}) => {
    const [selectedValue, setSelectedValue] = React.useState<{ id: number; label: string } | null>(null);

    useEffect(() => {
        const innendienstEmployee = getInnendienstEmployeeForWorker(worker, availableInnendienstEmployees);

        if (innendienstEmployee) {
            setSelectedValue({
                id: innendienstEmployee.id,
                label: `${innendienstEmployee.User.first_name} ${innendienstEmployee.User.last_name}`,
            });
        }
    }, [worker, availableInnendienstEmployees]);

    return (
        <Stack>
            <Autocomplete
                value={selectedValue}
                onChange={(_, newValue) => {
                    setSelectedValue(newValue);

                    if (newValue) {
                        const innendienstEmployee = availableInnendienstEmployees.find(
                            (employee) => employee.id === newValue.id,
                        );
                        if (innendienstEmployee) {
                            updateInnendienstAssignment(innendienstEmployee.id);
                        }
                    } else {
                        updateInnendienstAssignment(null);
                    }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={availableInnendienstEmployees.map((employee) => ({
                    label: `${employee.User.first_name} ${employee.User.last_name}`,
                    id: employee.id,
                }))}
                renderInput={(params) => (
                    <TextField {...params} key={params.id} size='small' label="Innendienst Mitarbeiter" placeholder="Mitarbeiter" />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.label}
                    </li>
                )}
            />
        </Stack>
    );
};

export default ProfileInnendienstAssignment;
